import {
  CBCustomer,
  EntityType,
} from "../../api/grpc/subscription/subscription";
import {
  ProductStoreAction,
  ProductStoreCatalogAction,
  ProductStorePriceDescriptor,
  ProductStorePricing,
  ProductStorePricingMode,
  ProductStorePurchaseRule,
} from "./ProductStore.types";
import { resolveProductPrice, resolveShipping } from "./productStorePrices";

export const PARTNER_PRICE_MESSAGE =
  "Your account is managed by your assigned partner. Request a quote for current pricing and purchasing options.";
export const PARTNER_QUOTE_LABEL = "Request partner quote";
export const PRICING_UNAVAILABLE_MESSAGE =
  "Pricing is unavailable right now. Refresh the page or contact sales.";
export const CONTACT_SALES_LABEL = "Contact sales";
export const PRICE_ON_REQUEST = "Price on request";
export const INCLUDED_IN_SUBSCRIPTION = "Included in your subscription";
export const MANAGE_IN_BILLING_LABEL = "Manage in Billing";

interface PartnerResult {
  loading: boolean;
  error?: unknown;
  data?: Pick<CBCustomer, "email">;
}

/**
 * Maps the channel-partner lookup to a pricing mode. Until the lookup has
 * returned (including the first render, before the request starts) the mode
 * is `loading`, so prices never flash for partner-managed customers.
 */
export const getPricingMode = ({
  loading,
  error,
  data,
}: PartnerResult): ProductStorePricingMode => {
  if (error) {
    return "unavailable";
  }

  if (loading || !data) {
    return "loading";
  }

  return data.email ? "partner" : "direct";
};

/** Prices (and links to change the plan) are only shown to direct customers. */
export const canSeePrices = (mode: ProductStorePricingMode) =>
  mode === "direct";

interface OfferInput {
  title: string;
  pricing?: ProductStorePriceDescriptor;
  priceNote?: string;
  ctaLabel: string;
  action: ProductStoreCatalogAction;
  entityType?: EntityType;
  purchase?: ProductStorePurchaseRule;
  addOnOnlyMessage?: string;
}

export interface ProductOffer {
  /** Price (or "Price on request") and note, only for direct customers. */
  price?: string;
  priceNote?: string;
  /** Shown instead of the price in `partner` and `unavailable` modes. */
  message?: string;
  /** Shows a placeholder instead of the price. */
  loading: boolean;
  ctaLabel: string;
  ctaDisabled: boolean;
  action: ProductStoreAction;
  /** Shown instead of the button, e.g. for products sold only as add-ons. */
  notice?: string;
}

/**
 * Turns a catalog action into one the page can dispatch. A checkout (one-time
 * or subscription) needs a price ID from the customer's prices; without one it
 * becomes a contact.
 */
const toAction = (
  action: ProductStoreCatalogAction,
  title: string,
  priceId?: string
): ProductStoreAction => {
  switch (action.type) {
    case "checkout":
    case "subscription":
      return priceId
        ? { type: action.type, productType: priceId }
        : { type: "contact", subject: title };
    case "contact":
      return { type: "contact", subject: title };
    default:
      return action;
  }
};

/** What a product card shows for the customer's pricing. */
export const getProductOffer = (
  { mode, prices, term, subscriptionAddOns = [] }: ProductStorePricing,
  {
    title,
    pricing,
    priceNote,
    ctaLabel,
    action,
    entityType,
    purchase,
    addOnOnlyMessage,
  }: OfferInput
): ProductOffer => {
  const contact: ProductStoreAction = { type: "contact", subject: title };

  if (mode === "partner") {
    return {
      message: PARTNER_PRICE_MESSAGE,
      loading: false,
      ctaLabel: PARTNER_QUOTE_LABEL,
      ctaDisabled: false,
      action: contact,
    };
  }

  if (mode === "unavailable") {
    return {
      message: PRICING_UNAVAILABLE_MESSAGE,
      loading: false,
      ctaLabel: CONTACT_SALES_LABEL,
      ctaDisabled: false,
      action: contact,
    };
  }

  if (mode === "loading" || prices.loading) {
    return {
      loading: true,
      ctaLabel,
      ctaDisabled: true,
      action: toAction(action, title),
    };
  }

  if (
    action.type === "subscription" &&
    entityType !== undefined &&
    subscriptionAddOns.includes(entityType)
  ) {
    return {
      price: INCLUDED_IN_SUBSCRIPTION,
      loading: false,
      ctaLabel: MANAGE_IN_BILLING_LABEL,
      ctaDisabled: false,
      action: { type: "billing" },
    };
  }

  const price = prices.error
    ? undefined
    : resolveProductPrice(pricing, prices, term);

  if (addOnOnlyMessage) {
    return {
      price: price?.text ?? PRICE_ON_REQUEST,
      priceNote,
      loading: false,
      ctaLabel,
      ctaDisabled: true,
      action: contact,
      notice: addOnOnlyMessage,
    };
  }

  // Shipping is mandatory: without its price the product can't be ordered.
  const shippingMissing =
    !!purchase?.shipping && !resolveShipping(purchase.shipping, prices);

  if (!price || shippingMissing) {
    return {
      price: PRICE_ON_REQUEST,
      priceNote,
      loading: false,
      ctaLabel: CONTACT_SALES_LABEL,
      ctaDisabled: false,
      action: contact,
    };
  }

  return {
    price: price.text,
    priceNote,
    loading: false,
    ctaLabel,
    ctaDisabled: false,
    action: toAction(action, title, price.priceId),
  };
};

/** Starting message for the Contact sales form, e.g. "Quote request: …". */
export const getQuoteRequestMessage = (subject?: string) =>
  subject ? `Quote request: ${subject}` : "";
